import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";
import { Plus, Search, X, BookUser, Trash2, Edit2, Phone, Mail, MapPin } from "lucide-react";

const SECOES = [
  { key: "", label: "Todos" },
  { key: "Justiça, tribunais e profissões jurídicas", label: "Justiça e Tribunais" },
  { key: "Registos, fiscal, segurança social, trabalho e imigração", label: "Registos e Fiscal" },
  { key: "Emergência, polícia e apoio a vítimas", label: "Emergência" },
  { key: "Reguladores, compliance e entidades úteis", label: "Reguladores" },
];

const SECAO_OPTIONS = SECOES.slice(1).map(s => s.key);

const EMPTY = { instituicao: "", nome_contato: "", telefone: "", telemovel: "", emails: "", secao: "", para_que: "", morada: "" };

export default function ContatosList() {
  const [contatos, setContatos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [secaoFiltro, setSecaoFiltro] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get(`${API}/contatos`, {
      params: { search, secao: secaoFiltro, limit: 200 },
      headers: getAuthHeader()
    }).then(({ data }) => {
      if (!active) return;
      setContatos(data.contatos);
      setTotal(data.total);
      setLoading(false);
    }).catch(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [search, secaoFiltro, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditId(null); setForm(EMPTY); setShowModal(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({ instituicao: c.instituicao || "", nome_contato: c.nome_contato || "", telefone: c.telefone || "", telemovel: c.telemovel || "", emails: c.emails || "", secao: c.secao || "", para_que: c.para_que || "", morada: c.morada || "" });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editId) { await axios.put(`${API}/contatos/${editId}`, form, { headers: getAuthHeader() }); }
      else { await axios.post(`${API}/contatos`, form, { headers: getAuthHeader() }); }
      setShowModal(false); setRefresh(r => r + 1);
    } catch (err) { alert(err.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar contato?")) return;
    await axios.delete(`${API}/contatos/${id}`, { headers: getAuthHeader() });
    setRefresh(r => r + 1);
  };

  // Agrupar por secção quando não há filtro activo
  const grouped = secaoFiltro || search
    ? null
    : SECOES.slice(1).map(s => ({
        secao: s,
        items: contatos.filter(c => c.secao === s.key),
      })).filter(g => g.items.length > 0);

  const renderRow = (c) => (
    <tr key={c.id} data-testid={`contato-row-${c.id}`} className="hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3">
        <p className="font-medium text-slate-900 text-sm">{c.instituicao}</p>
        {c.para_que && <p className="text-xs text-slate-500 mt-0.5">{c.para_que}</p>}
        {c.morada && (
          <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
            <MapPin size={10}/>{c.morada}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        {c.telefone ? (
          <div className="flex items-start gap-1 text-sm text-slate-600">
            <Phone size={12} className="mt-0.5 flex-shrink-0 text-slate-400"/>
            <span>{c.telefone}</span>
          </div>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-4 py-3">
        {c.emails ? (
          <div className="flex items-start gap-1 text-sm text-slate-600">
            <Mail size={12} className="mt-0.5 flex-shrink-0 text-slate-400"/>
            <span className="break-all">{c.emails}</span>
          </div>
        ) : <span className="text-slate-300">—</span>}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <button onClick={() => openEdit(c)} className="text-slate-400 hover:text-slate-600 p-1 transition-colors"><Edit2 size={14}/></button>
          <button onClick={() => handleDelete(c.id)} className="text-slate-400 hover:text-red-500 p-1 transition-colors"><Trash2 size={14}/></button>
        </div>
      </td>
    </tr>
  );

  const renderTable = (items) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>{["Entidade / Para quê", "Telefone", "Email", "Ações"].map(h => (
            <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">{h}</th>
          ))}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map(renderRow)}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><BookUser size={22} className="text-[#0b679e]"/> Contatos</h1>
          <p className="text-slate-500 text-sm mt-0.5">{total} contato(s)</p>
        </div>
        <button onClick={openNew} data-testid="novo-contato-btn"
          className="flex items-center gap-2 bg-[#0b679e] hover:bg-[#084e78] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus size={16}/> Novo Contato
        </button>
      </div>

      {/* Pesquisa + Tabs */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="relative max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={search} onChange={e => { setSearch(e.target.value); }}
            placeholder="Pesquisar entidade, função..."
            data-testid="search-contatos"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
        </div>
        <div className="flex flex-wrap gap-2">
          {SECOES.map(s => (
            <button key={s.key} onClick={() => setSecaoFiltro(s.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${secaoFiltro === s.key ? "bg-[#0b679e] text-white border-[#0b679e]" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Conteúdo */}
      {(() => {
        if (loading) return (
          <div className="bg-white rounded-xl border border-slate-200 flex items-center justify-center h-40">
            <div className="w-7 h-7 border-4 border-[#0b679e] border-t-[#F5A623] rounded-full animate-spin"/>
          </div>
        );

        if (contatos.length === 0) return (
          <div className="bg-white rounded-xl border border-slate-200 text-center py-16 text-slate-400">
            <BookUser size={40} className="mx-auto mb-3 opacity-30"/>
            <p>Sem contatos encontrados</p>
          </div>
        );

        // Sem filtro de secção: agrupar por secção com cabeçalho
        if (grouped) return (
          <div className="space-y-4">
            {grouped.map(({ secao, items }) => (
              <div key={secao.key} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                  <span className="text-xs font-bold text-[#0b679e] uppercase tracking-wider">{secao.label}</span>
                  <span className="text-xs text-slate-400">({items.length})</span>
                </div>
                {renderTable(items)}
              </div>
            ))}
          </div>
        );

        // Com filtro: tabela simples
        return (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {renderTable(contatos)}
          </div>
        );
      })()}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{editId ? "Editar Contato" : "Novo Contato"}</h3>
              <button onClick={() => setShowModal(false)}><X size={18}/></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Secção</label>
                <select value={form.secao} onChange={e => setForm({ ...form, secao: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]">
                  <option value="">— Sem secção —</option>
                  {SECAO_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Instituição/Empresa *</label>
                <input value={form.instituicao} onChange={e => setForm({ ...form, instituicao: e.target.value })} required data-testid="contato-instituicao"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Para quê</label>
                <input value={form.para_que} onChange={e => setForm({ ...form, para_que: e.target.value })}
                  placeholder="Finalidade ou área de contacto"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Nome do Contato</label>
                <input value={form.nome_contato} onChange={e => setForm({ ...form, nome_contato: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Telefone</label>
                  <input value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Telemóvel</label>
                  <input value={form.telemovel} onChange={e => setForm({ ...form, telemovel: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Email(s)</label>
                <textarea value={form.emails} onChange={e => setForm({ ...form, emails: e.target.value })} rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Morada / Notas</label>
                <input value={form.morada} onChange={e => setForm({ ...form, morada: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]"/>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm">Cancelar</button>
                <button type="submit" disabled={saving} data-testid="save-contato-btn"
                  className="px-4 py-2 bg-[#0b679e] hover:bg-[#084e78] text-white rounded-lg text-sm font-medium disabled:opacity-60">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
